import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { getConsoleEntries, getNetworkEntries } from '../buffers';
import { collectEnvironment, collectPage } from '../environment';
import { categoryHint, createTranslator, type Language } from '../i18n';
import { activatePicker } from '../picker';
import { captureScreenshot } from '../screenshot';
import type { PickedElement, Reporter, WidgetSettings } from '../types';
import { ChatIcon, CheckIcon, CloseIcon, CursorIcon } from './icons';
import { supportsHover, useDraggableLauncher } from './use-draggable-launcher';

const MIN_DESCRIPTION_LENGTH = 5;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Stage = 'category' | 'details' | 'sending' | 'done';

interface WidgetAppProps {
  settings: WidgetSettings;
  language: Language;
  publicKey: string;
  apiUrl: string;
  assetOrigin: string;
  getReporter: () => Reporter | null;
  getMetadata: () => Record<string, unknown> | null;
  registerOpener: (open: () => void) => void;
}

export function WidgetApp({
  settings,
  language,
  publicKey,
  apiUrl,
  assetOrigin,
  getReporter,
  getMetadata,
  registerOpener,
}: WidgetAppProps) {
  const t = createTranslator(language);

  const [open, setOpen] = useState(false);
  // Começa recolhido de propósito: é o estado de "fora de foco", para não
  // tapar o que está embaixo antes mesmo de alguém interagir com a página.
  const [collapsed, setCollapsed] = useState(true);
  const [hoverCapable] = useState(supportsHover);
  const drag = useDraggableLauncher(publicKey);
  const [stage, setStage] = useState<Stage>('category');
  const [category, setCategory] = useState(settings.categories[0]?.id ?? '');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [picked, setPicked] = useState<PickedElement | null>(null);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancelPickerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    registerOpener(() => setOpen(true));
  }, [registerOpener]);

  // Sair do modo de seleção junto com o widget evita deixar a página do cliente
  // presa em cursor de mira caso o script seja removido no meio.
  useEffect(() => () => cancelPickerRef.current?.(), []);

  // Fechar o painel devolve o botão ao estado "fora de foco": sem isto, quem
  // fechava com o mouse ainda sobre o canto via o botão nascer expandido.
  useEffect(() => {
    if (!open) setCollapsed(true);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const reset = useCallback(() => {
    setStage('category');
    setDescription('');
    setPicked(null);
    setScreenshot(null);
    setError(null);
    setCategory(settings.categories[0]?.id ?? '');
  }, [settings.categories]);

  const startPicking = useCallback(() => {
    // O painel some enquanto a pessoa aponta: ele cobre justamente a parte da
    // tela que ela pode querer marcar.
    setOpen(false);

    cancelPickerRef.current = activatePicker({
      hint: t('pointing'),
      accentColor: settings.accent_color,
      onPick: async (element) => {
        cancelPickerRef.current = null;

        if (!element) {
          setOpen(true);
          return;
        }

        setPicked(element);
        setStage('details');

        if (settings.capture_screenshot) {
          setCapturing(true);
          // Espera o próximo quadro para que os adornos do picker já tenham
          // saído da tela antes da captura.
          await new Promise((resolve) => requestAnimationFrame(resolve));
          setScreenshot(
            await captureScreenshot({
              marker: element.rect,
              accentColor: settings.accent_color,
              assetOrigin,
            }),
          );
          setCapturing(false);
        }

        setOpen(true);
      },
    });
  }, [settings.accent_color, settings.capture_screenshot, assetOrigin, t]);

  async function submit() {
    if (!category) {
      setError(t('categoryRequired'));
      return;
    }
    if (description.trim().length < MIN_DESCRIPTION_LENGTH) {
      setError(t('descriptionTooShort'));
      return;
    }

    const trimmedEmail = email.trim();
    if ((settings.require_email || trimmedEmail) && !EMAIL_PATTERN.test(trimmedEmail)) {
      setError(t('emailInvalid'));
      return;
    }

    setStage('sending');
    setError(null);

    const identified = getReporter();
    const reporter: Reporter | null =
      identified || trimmedEmail ? { ...identified, ...(trimmedEmail ? { email: trimmedEmail } : {}) } : null;

    try {
      const response = await fetch(`${apiUrl}/api/v1/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          public_key: publicKey,
          category,
          description: description.trim(),
          page: collectPage(),
          environment: collectEnvironment(),
          element: picked,
          reporter,
          screenshot,
          console_logs: settings.capture_console ? getConsoleEntries() : [],
          network_errors: settings.capture_network ? getNetworkEntries() : [],
          custom_data: getMetadata(),
        }),
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { detail?: string } | null;
        setError(payload?.detail ?? t('errorTitle'));
        setStage('details');
        return;
      }

      setStage('done');
    } catch {
      setError(t('errorTitle'));
      setStage('details');
    }
  }

  if (!open) {
    const label = language === 'pt-BR' ? settings.launcher_label_pt : settings.launcher_label_en;
    const somenteIcone = settings.launcher_shape === 'circle';
    // Arrastando, o botão expande mesmo sem hover: senão vira alvo pequeno
    // demais para acompanhar o dedo.
    const isCollapsed = collapsed && !drag.dragging;
    // No colapsado o ícone é a única pista do que aquela bolinha é, mesmo
    // quando o cliente desligou o ícone no modo expandido. Vale também
    // arrastando, para não sumir no meio do gesto só porque o botão cresceu.
    const showIcon = settings.launcher_show_icon || collapsed;

    const classes = ['launcher'];
    if (isCollapsed) classes.push('collapsed');
    if (drag.dragging) classes.push('dragging');

    return (
      <button
        ref={drag.ref}
        type="button"
        class={classes.join(' ')}
        style={drag.offset ? { transform: `translate3d(${drag.offset.x}px, ${drag.offset.y}px, 0)` } : undefined}
        onPointerDown={drag.onPointerDown}
        onMouseEnter={hoverCapable ? () => setCollapsed(false) : undefined}
        onMouseLeave={hoverCapable ? () => setCollapsed(true) : undefined}
        onClick={() => {
          if (drag.consumeDragClick()) return;
          setOpen(true);
        }}
        aria-label={label}
      >
        {showIcon ? <ChatIcon /> : null}
        {somenteIcone ? null : (
          <span class="launcher-label" aria-hidden="true">
            {label}
          </span>
        )}
      </button>
    );
  }

  return (
    <div class="panel" role="dialog" aria-modal="false" aria-label={t('title')}>
      {/* O fechar fica fora do condicional de estágio. Quando ele vivia só no
          ramo do formulário, a confirmação não tinha saída nenhuma: quem estava
          no telefone, sem tecla Escape, ficava preso com o painel na tela. */}
      <div class="header">
        {stage === 'done' ? (
          <span />
        ) : stage === 'details' ? (
          <button type="button" class="back" onClick={() => setStage('category')}>
            {t('categoryLabel')}
          </button>
        ) : (
          <h2 class="title">{t('stepTitle')}</h2>
        )}
        <button type="button" class="close" onClick={() => setOpen(false)} aria-label={t('close')}>
          <CloseIcon />
        </button>
      </div>

      {stage === 'done' ? (
        <div class="done">
          <span class="done-icon">
            <CheckIcon />
          </span>
          <h2>{t('successTitle')}</h2>
          <p>{t('successBody')}</p>
          <div class="done-actions">
            <button type="button" class="link" onClick={reset}>
              {t('sendAnother')}
            </button>
            <button type="button" class="link" onClick={() => setOpen(false)}>
              {t('close')}
            </button>
          </div>
        </div>
      ) : stage === 'category' ? (
        <>
          <div class="categories">
            {settings.categories.map((item) => {
              const hint = categoryHint(language, item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  class="category"
                  data-category={item.id}
                  aria-pressed={category === item.id}
                  onClick={() => setCategory(item.id)}
                >
                  <span class="dot" />
                  <span class="category-copy">
                    <span class="category-name">
                      {language === 'pt-BR' ? item.label_pt : item.label_en}
                    </span>
                    {hint ? <span class="category-hint">{hint}</span> : null}
                  </span>
                </button>
              );
            })}
          </div>

          <button type="button" class="submit" onClick={startPicking}>
            <CursorIcon />
            {t('pointAction')}
          </button>
          <button type="button" class="link block-link" onClick={() => setStage('details')}>
            {t('skipPoint')}
          </button>
          <p class="brand">{t('poweredBy')}</p>
        </>
      ) : (
        <>
          {picked ? (
            <div class="field">
              <div class="picked">
                <code>{picked.text ?? picked.selector}</code>
                <button type="button" class="link" onClick={startPicking}>
                  {t('changeElement')}
                </button>
              </div>
              {capturing ? <p class="hint">{t('capturing')}</p> : null}
              {screenshot ? <img class="preview" src={screenshot} alt={t('pointed')} /> : null}
            </div>
          ) : (
            <button type="button" class="point" onClick={startPicking}>
              <CursorIcon />
              {t('pointAction')}
            </button>
          )}

          <div class="field">
            <label class="label" for="clixy-description">
              {t('descriptionLabel')}
            </label>
            <textarea
              id="clixy-description"
              class="textarea"
              value={description}
              placeholder={t('descriptionPlaceholder')}
              onInput={(event) => setDescription((event.target as HTMLTextAreaElement).value)}
            />
          </div>

          {settings.require_email || email ? (
            <div class="field">
              <label class="label" for="clixy-email">
                {t('emailLabel')}
              </label>
              <input
                id="clixy-email"
                class="input"
                type="email"
                value={email}
                placeholder={t('emailPlaceholder')}
                onInput={(event) => setEmail((event.target as HTMLInputElement).value)}
              />
            </div>
          ) : (
            <button type="button" class="link" onClick={() => setEmail(' ')}>
              {t('emailLabel')}
            </button>
          )}

          <div class="goes">
            <p class="label">{t('goesWith')}</p>
            <ul>
              {settings.capture_screenshot ? <li>{t('withScreenshot')}</li> : null}
              {settings.capture_console ? <li>{t('withConsole')}</li> : null}
              {settings.capture_network ? <li>{t('withNetwork')}</li> : null}
              <li>{t('withEnvironment')}</li>
            </ul>
          </div>

          {error ? <p class="error">{error}</p> : null}

          <button
            type="button"
            class="submit"
            disabled={stage === 'sending' || capturing}
            onClick={submit}
          >
            {stage === 'sending' ? t('sending') : t('send')}
          </button>
        </>
      )}
    </div>
  );
}
